import { useMutation } from "@tanstack/react-query";

import { queryClient } from "#app/http/ReactQueryProvider";
import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import { layoutQuery } from "#modules/layouts/infra/layout.query";
import { isOptimizationInvalidationCancelled } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { partQuery } from "#modules/parts/infra/part.query";
import { partJsonQuery } from "#modules/parts/infra/partJson.query";
import {
  PartDimensionsDto,
  updatePartDimensions,
} from "#modules/parts/infra/updatePartDimensions.api";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { quoteRequestQuery } from "#modules/quoteRequests/infra/quoteRequest.query";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { JobStatusSchema, QuoteRequestLine } from "shared";

export const useUpdatePartDimensions = ({
  partId,
  quoteRequestId,
  quoteRequestLine,
  layoutId,
  options,
}: {
  partId: string;
  quoteRequestId?: string;
  quoteRequestLine: QuoteRequestLine;
  layoutId: string | null;
  options?: {
    onSuccess?: () => void;
  };
}) => {
  const analytics = useAnalytics();
  const { company } = useCompany();

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PartDimensionsDto }) =>
      updatePartDimensions(id, data),
    onMutate: () => {
      if (quoteRequestLine.predictionStatus === JobStatusSchema.enum.PROCESSED) {
        analytics.capture(AnalyticsEvent.PredictionRerun, {
          quote_request_line_id: quoteRequestLine.id,
          trigger: "updatePartDimensions",
          company_id: company?.id ?? null,
          company_name: company?.name ?? null,
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(quoteRequestQuery(quoteRequestId || ""));
      void queryClient.invalidateQueries(partQuery(partId));
      void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLine.id }));
      void queryClient.invalidateQueries(layoutQuery(layoutId || ""));
      void queryClient.invalidateQueries(partJsonQuery(partId));

      options?.onSuccess?.();
    },
    onError: (err) => {
      if (isOptimizationInvalidationCancelled(err)) {
        throw err;
      }

      showErrorToast({
        title: "Erreur est survenue",
        message: "Erreur est survenue lors de la mise à jour des dimensions de la pièce",
      });
      throw err;
    },
  });

  return { updatePartDimensions: mutate, updatePartDimensionsAsync: mutateAsync, ...rest };
};
